import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { CollectionSettings } from "@shared/schema";

const DEFAULT_FORM = {
  negativeBalanceThreshold: "-299",
  paymentPercentage: "10",
  paymentFrequency: "weekly",
  paymentDeadlineDays: "7",
  offerLimit: "3",
  cooldownDays: "7",
  offerResetDays: "",
};

export function SettingsTab({ readOnly }: { readOnly: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  const settingsQuery = useQuery<CollectionSettings | null>({
    queryKey: ["/api/app/collections/settings"],
  });
  const settings = settingsQuery.data;

  useEffect(() => {
    if (settings) {
      setForm({
        negativeBalanceThreshold: settings.negativeBalanceThreshold ?? "-299",
        paymentPercentage: settings.paymentPercentage ?? "10",
        paymentFrequency: settings.paymentFrequency ?? "weekly",
        paymentDeadlineDays: String(settings.paymentDeadlineDays ?? 7),
        offerLimit: String(settings.offerLimit ?? 3),
        cooldownDays: String(settings.cooldownDays ?? 7),
        offerResetDays: settings.offerResetDays != null ? String(settings.offerResetDays) : "",
      });
    }
  }, [settings]);

  const thresholdValue = parseFloat(form.negativeBalanceThreshold);
  const thresholdInvalid = isNaN(thresholdValue) || thresholdValue < -299 || thresholdValue > 100;

  const enabled = settings?.enabled ?? false;

  const toggleMutation = useMutation({
    mutationFn: async (nextEnabled: boolean) => {
      const res = await apiRequest("PUT", "/api/app/collections/settings", { enabled: nextEnabled });
      return res.json();
    },
    onSuccess: (_data, nextEnabled) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] });
      toast({
        title: nextEnabled ? "Collections turned on" : "Collections turned off",
        description: nextEnabled
          ? "Enabled properties will be processed on the daily run."
          : "No collection messages will be sent until you turn this back on.",
      });
    },
    onError: (e: Error) => toast({
      title: "Couldn't update collections",
      description: parseErrorMessage(e, "Couldn't change the collections master switch. Please try again."),
      variant: "destructive",
    }),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const threshold = parseFloat(data.negativeBalanceThreshold);
      if (isNaN(threshold) || threshold < -299 || threshold > 100) {
        throw new Error("Threshold must be between -$299 and $100.");
      }
      const res = await apiRequest("PUT", "/api/app/collections/settings", {
        negativeBalanceThreshold: data.negativeBalanceThreshold,
        paymentPercentage: data.paymentPercentage,
        paymentFrequency: data.paymentFrequency,
        paymentDeadlineDays: parseInt(data.paymentDeadlineDays, 10),
        offerLimit: parseInt(data.offerLimit, 10),
        cooldownDays: parseInt(data.cooldownDays, 10),
        // Blank = count offers all-time (the historical behaviour).
        offerResetDays: String(data.offerResetDays ?? "").trim() === "" ? null : parseInt(data.offerResetDays, 10),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] });
      toast({ title: "Settings saved", description: "Account collection defaults have been updated." });
    },
    onError: (e: Error) => toast({
      title: "Error",
      description: parseErrorMessage(e, "Couldn't save collection settings. Please try again."),
      variant: "destructive",
    }),
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="collections-settings-tab">
      {/* Master switch — turning this on begins automated messaging to members
          about money they owe, so it confirms first. */}
      <div className="mb-8 rounded-lg border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Automatic collections</h2>
            <p className="mt-1 text-sm text-gray-500">
              When on, enabled properties are processed automatically each day. When off, nothing sends — including
              manual runs.
            </p>
          </div>
          <Switch
            checked={enabled}
            disabled={readOnly || toggleMutation.isPending}
            onCheckedChange={(next) => {
              if (next) setConfirmEnableOpen(true);
              else toggleMutation.mutate(false);
            }}
            aria-label="Enable automatic collections"
            data-testid="switch-collections-enabled"
          />
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Account defaults</h2>
        <p className="mt-1 text-sm text-gray-500">
          Baseline rules for every property. Individual properties can override any of these.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="threshold">Maximum Negative Balance Threshold</Label>
          <p className="mb-2 text-xs text-gray-500">
            Members with a balance at or beyond this threshold will be contacted. Range: -$299 (most negative) to $100.
          </p>
          <Input
            id="threshold"
            type="number"
            min="-299"
            max="100"
            value={form.negativeBalanceThreshold}
            onChange={(e) => setForm({ ...form, negativeBalanceThreshold: e.target.value })}
            placeholder="-299"
            className="w-40"
            data-testid="input-threshold"
          />
          {thresholdInvalid && form.negativeBalanceThreshold !== "" && (
            <p className="mt-1 text-xs text-red-500">Must be between -$299 and $100</p>
          )}
        </div>

        <div>
          <Label htmlFor="percentage">Required Payment Percentage</Label>
          <p className="mb-2 text-xs text-gray-500">
            The percentage of the outstanding balance the member must pay as a partial payment.
          </p>
          <div className="flex items-center gap-2">
            <Input
              id="percentage"
              type="number"
              min="1"
              max="100"
              value={form.paymentPercentage}
              onChange={(e) => setForm({ ...form, paymentPercentage: e.target.value })}
              className="w-28"
              data-testid="input-payment-percentage"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </div>

        <div>
          <Label>Payment Frequency</Label>
          <p className="mb-2 text-xs text-gray-500">How often the partial payment must be made.</p>
          <Select value={form.paymentFrequency} onValueChange={(v) => setForm({ ...form, paymentFrequency: v })}>
            <SelectTrigger className="w-48" data-testid="select-payment-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="deadline">Payment Deadline (Days)</Label>
          <p className="mb-2 text-xs text-gray-500">How many days the member has to make each payment.</p>
          <div className="flex items-center gap-2">
            <Input
              id="deadline"
              type="number"
              min="1"
              value={form.paymentDeadlineDays}
              onChange={(e) => setForm({ ...form, paymentDeadlineDays: e.target.value })}
              className="w-28"
              data-testid="input-payment-deadline"
            />
            <span className="text-sm text-gray-500">days</span>
          </div>
        </div>

        <div>
          <Label htmlFor="offer-limit">Offer Limit</Label>
          <p className="mb-2 text-xs text-gray-500">
            Maximum number of times a member can receive the payment arrangement offer before escalation.
          </p>
          <Input
            id="offer-limit"
            type="number"
            min="1"
            value={form.offerLimit}
            onChange={(e) => setForm({ ...form, offerLimit: e.target.value })}
            className="w-28"
            data-testid="input-offer-limit"
          />
        </div>

        <div>
          <Label htmlFor="cooldown">Cooldown (Days)</Label>
          <p className="mb-2 text-xs text-gray-500">
            Minimum days between notices to the same member, so someone in arrears isn't messaged daily.
          </p>
          <Input
            id="cooldown"
            type="number"
            min="0"
            value={form.cooldownDays}
            onChange={(e) => setForm({ ...form, cooldownDays: e.target.value })}
            className="w-28"
            data-testid="input-cooldown-days"
          />
        </div>

        <div>
          <Label htmlFor="offer-reset">Offer Reset Window (Days)</Label>
          <p className="mb-2 text-xs text-gray-500">
            Only count offers sent within this many days against the offer limit. Leave blank to count all-time.
          </p>
          <Input
            id="offer-reset"
            type="number"
            min="1"
            value={form.offerResetDays}
            placeholder="All-time"
            onChange={(e) => setForm({ ...form, offerResetDays: e.target.value })}
            className="w-28"
            data-testid="input-offer-reset-days"
          />
        </div>

        <div className="pt-2">
          <Button
            className="bg-brand hover:bg-brand-hover text-white"
            onClick={() => saveMutation.mutate(form)}
            disabled={readOnly || saveMutation.isPending || thresholdInvalid}
            data-testid="button-save-collection-settings"
          >
            {saveMutation.isPending ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmEnableOpen} onOpenChange={setConfirmEnableOpen}>
        <AlertDialogContent data-testid="dialog-confirm-enable">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on automatic collections?</AlertDialogTitle>
            <AlertDialogDescription>
              This will automatically send payment-arrangement messages to members with outstanding balances at every
              property you have enabled. Use Preview on the Properties tab first if you want to see exactly who would be
              contacted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-enable">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toggleMutation.mutate(true)}
              data-testid="button-confirm-enable"
            >
              Turn on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
